import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API from '../../api/API';
import { idDaSlug } from './links';
import NonTrovato from './NonTrovato';
import SchedaCarriera from '../../components/statistiche/SchedaCarriera';
import RigaKpi from '../../components/statistiche/RigaKpi';
import TabellaOrdinabile from '../../components/statistiche/TabellaOrdinabile';
import Piazzamento from '../../components/statistiche/Piazzamento';
import { LinkEdizione, LinkSquadra, LinkPartita } from '../../components/statistiche/Link';
// Breadcrumb, caricamento ed errore hanno già le loro regole nel foglio della sezione: qui si
// riusano invece di ridefinirle, così una modifica al breadcrumb non va fatta in due posti.
import './Statistiche.css';
import './Scheda.css';

// Le colonne stanno fuori dal componente perché TabellaOrdinabile tiene lo stato dell'ordinamento
// e un array ricreato a ogni render lo farebbe ripartire invece di aggiornarlo.
const colonneEdizioni = [
  {
    chiave: 'edizione', etichetta: 'Edizione', testo: true,
    valore: (r) => `${r.torneo} ${r.edizione}`,
    rende: (r) => <LinkEdizione id={r.idEdizione}>{`${r.torneo} ${r.edizione}`}</LinkEdizione>,
  },
  {
    chiave: 'squadra', etichetta: 'Squadra', testo: true,
    valore: (r) => r.squadra,
    rende: (r) => <LinkSquadra slug={r.slugSquadra} nome={r.squadra} />,
  },
  {
    chiave: 'posizione', etichetta: 'Pos.', etichettaLunga: 'Piazz.', soloTabella: true,
    valore: (r) => r.posizione ?? 0,
    rende: (r) => <Piazzamento posizione={r.posizione} />,
  },
  { chiave: 'partite', etichetta: 'P', etichettaLunga: 'Partite', valore: (r) => r.partite },
  { chiave: 'mete', etichetta: 'M', etichettaLunga: 'Mete', valore: (r) => r.mete },
  { chiave: 'assist', etichetta: 'A', etichettaLunga: 'Assist', valore: (r) => r.assist },
  { chiave: 'pieni', etichetta: 'Pi', etichettaLunga: 'Pieni', valore: (r) => r.pieni },
  { chiave: 'vuoti', etichetta: 'V', etichettaLunga: 'Vuoti', valore: (r) => r.vuoti },
];

const nomePartita = (r) => r.nome || r.fase;

const colonnePartite = [
  {
    chiave: 'partita', etichetta: 'Partita', testo: true,
    valore: nomePartita,
    rende: (r) => <LinkPartita idEdizione={r.idEdizione} idPartita={r.idPartita}>{nomePartita(r)}</LinkPartita>,
  },
  {
    chiave: 'avversario', etichetta: 'Avversario', testo: true,
    valore: (r) => r.avversario,
    rende: (r) => <LinkSquadra slug={r.slugAvversario} nome={r.avversario} />,
  },
  {
    chiave: 'esito', etichetta: 'Esito', ordinabile: false,
    valore: (r) => r.meteSquadra,
    rende: (r) => `${r.meteSquadra}–${r.meteAvversario}`,
  },
  { chiave: 'mete', etichetta: 'M', etichettaLunga: 'Mete', valore: (r) => r.mete },
  { chiave: 'assist', etichetta: 'A', etichettaLunga: 'Assist', valore: (r) => r.assist },
  { chiave: 'pieni', etichetta: 'Pi', etichettaLunga: 'Pieni', valore: (r) => r.pieni },
  { chiave: 'vuoti', etichetta: 'V', etichettaLunga: 'Vuoti', valore: (r) => r.vuoti },
];

// Le colonne testuali non compaiono nella forma a card, quindi i link che vivono nel loro `rende`
// esistono solo su desktop: senza questi quattro, da telefono il nome della riga sarebbe testo
// morto e la navigazione fra le schede sparirebbe proprio dove serve di più.
const titoloEdizione = (r) => <LinkEdizione id={r.idEdizione}>{`${r.torneo} ${r.edizione}`}</LinkEdizione>;
const sottotitoloEdizione = (r) => <><LinkSquadra slug={r.slugSquadra} nome={r.squadra} />{r.numero ? ` · #${r.numero}` : ''}</>;
const titoloPartita = (r) => <LinkPartita idEdizione={r.idEdizione} idPartita={r.idPartita}>{nomePartita(r)}</LinkPartita>;
const sottotitoloPartita = (r) => <>vs <LinkSquadra slug={r.slugAvversario} nome={r.avversario} /></>;

// Una persona può aver giocato più edizioni con la stessa squadra: nella colonna identità la
// squadra va nominata una volta con il conto delle edizioni, non ripetuta anno per anno.
// Il raggruppamento è per slug — cioè per squadra storica — e non per nome: due edizioni diverse
// possono scrivere il nome dell'iscrizione in modo diverso, ed è l'id a dire che sono la stessa
// squadra, non la coincidenza delle due scritture.
const squadreDistinte = (edizioni) =>
  edizioni.reduce((acc, e) => {
    // Una squadra senza storica non ha uno slug: tenerne conto qui fonderebbe in una riga sola
    // tutte quelle a cui non è stata ancora assegnata una identità nel tempo.
    if (!e.slugSquadra) return acc;
    const trovata = acc.find((s) => s.slug === e.slugSquadra);
    if (trovata) trovata.edizioni += 1;
    // Il nome è quello della storica, non quello scritto su questa iscrizione: un aggregato
    // cross-torneo deve restare corretto anche se la storica viene rinominata dopo.
    else acc.push({ slug: e.slugSquadra, nome: e.nomeStorica, edizioni: 1 });
    return acc;
  }, []);

/**
 * La carriera di una persona attraverso i tornei. È la pagina che prima non esisteva: le sue
 * statistiche si potevano vedere solo dentro una edizione per volta.
 *
 * L'id si legge dallo slug: è lui a risolvere, il resto del segmento serve solo a rendere il
 * link leggibile. Uno slug senza id in testa non è un indirizzo valido e non merita una
 * chiamata di rete.
 */
export default function SchedaGiocatore() {
  const { idSlug } = useParams();
  const id = idDaSlug(idSlug);

  const [carriera, setCarriera] = useState(null);
  const [caricando, setCaricando] = useState(!!id);
  const [errore, setErrore] = useState(null);

  const carica = useCallback(async () => {
    if (!id) return;
    setCaricando(true);
    setErrore(null);
    try {
      const r = await API.carriera.giocatore(id);
      setCarriera(r.isSuccess ? r.data : null);
    } catch (e) {
      setErrore(e.message || 'Errore nel caricamento');
    } finally {
      setCaricando(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  if (!id) return <NonTrovato cosa="Questo giocatore" />;
  if (caricando) return <div className="statistiche-loading">Caricamento…</div>;

  if (errore) {
    return (
      <div className="statistiche-error">
        <h3 className="statistiche-error-title">Errore nel caricamento</h3>
        <p className="statistiche-error-text">{errore}</p>
        <button onClick={carica} className="statistiche-retry-button">Riprova</button>
      </div>
    );
  }

  if (!carriera) return <NonTrovato cosa="Questo giocatore" />;

  const totali = carriera.totali ?? {};
  const edizioni = carriera.edizioni ?? [];
  const partite = carriera.partite ?? [];
  const gruppi = carriera.gruppi ?? [];
  const squadre = squadreDistinte(edizioni);

  const contoEdizioni = totali.edizioni ?? edizioni.length;
  // Il gruppo scout è testo libero e in vent'anni la stessa persona lo ha visto scritto in
  // quattro modi ("imola 4", "imola", "im", "C Z IMOLA"): elencarli tutti riempie la riga di
  // rumore. Se ne mostra uno, il più recente, e gli altri restano nel title per chi li cerca.
  const gruppoPrincipale = gruppi[0];
  const sottotitolo = [
    gruppoPrincipale,
    `${contoEdizioni} ${contoEdizioni === 1 ? 'edizione' : 'edizioni'}`,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <SchedaCarriera
        titolo={`${carriera.nome} ${carriera.cognome}`}
        sottotitolo={sottotitolo}
        kpi={<RigaKpi voci={[
          { chiave: 'partite', etichetta: 'Part.', etichettaLunga: 'Partite', valore: totali.partite },
          { chiave: 'mete', etichetta: 'Mete', valore: totali.mete },
          { chiave: 'assist', etichetta: 'Assist', valore: totali.assist },
          { chiave: 'pieni', etichetta: 'Pieni', valore: totali.pieni },
          { chiave: 'vuoti', etichetta: 'Vuoti', valore: totali.vuoti },
        ]} />}
        lato={squadre.length > 0 ? (
          <div className="scheda-lato-blocco">
            <h2 className="scheda-lato-titolo">Squadre</h2>
            <ul className="scheda-lato-elenco">
              {squadre.map((s) => (
                <li key={s.slug}>
                  <LinkSquadra slug={s.slug} nome={s.nome} />
                  <span className="scheda-lato-conto">{s.edizioni} ed.</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      >
        <section>
          <h2 className="scheda-sezione-titolo">Per edizione</h2>
          <TabellaOrdinabile
            colonne={colonneEdizioni}
            righe={edizioni}
            chiaveRiga={(r) => r.idEdizione}
            ordineIniziale={{ campo: 'edizione', verso: 'desc' }}
            titoloCard={titoloEdizione}
            sottotitoloCard={sottotitoloEdizione}
            badgeCard={(r) => <Piazzamento posizione={r.posizione} />}
            vuoto="Nessuna edizione giocata"
          />
        </section>

        {/* Il referto per partita c'è solo per chi ne ha: una carriera senza partite mostra i
            totali e le edizioni, non una sezione vuota. */}
        {partite.length > 0 && (
          <section>
            <h2 className="scheda-sezione-titolo">Partite giocate</h2>
            <TabellaOrdinabile
              colonne={colonnePartite}
              righe={partite}
              chiaveRiga={(r) => r.idPartita}
              ordineIniziale={{ campo: 'mete', verso: 'desc' }}
              titoloCard={titoloPartita}
              sottotitoloCard={sottotitoloPartita}
              badgeCard={(r) => `${r.meteSquadra}–${r.meteAvversario}`}
            />
          </section>
        )}
      </SchedaCarriera>
    </>
  );
}
